/**
 * Las colillas de pago (the owner, 2026-11-01: «we need a function to print paystubs for each employee
 * from the payroll page»): for each person on a run, a settlement or an aguinaldo payment, what they
 * earned, what came off it and what they receive, one stub each.
 *
 * What a stub says is decided once (`colillaDePlanilla`, `colillaDeAguinaldo`) and drawn two ways:
 *
 *   · as text, as a receipt is (`recibo.ts`): thirty-two characters a line, printed on the till's roll
 *     (`imprimir.ts`) or sent over WhatsApp (`textoDeColilla`);
 *   · as a PDF to download (the owner, 2026-11-01: «can we also have a function that just makes a pdf
 *     for the pay stub for download»), a page of US Letter each, drawn by the app's own PDF writer
 *     (`pdf.ts`) as the price catalogue is (`colillasEnPdf`).
 *
 * Spanish whatever language the screen is in, as a receipt is for the customer: the stub is for the
 * person paid. On paper it ends with a line for them to sign that they received it, which the shop
 * keeps; over WhatsApp it goes without.
 *
 * Every figure is the line's own, as the books booked it (`PlanillaLinea`), never worked out again: a
 * stub of March keeps saying what March paid, whatever the rates, the salary or the settings since. Its
 * earnings add up to the line's `brutoTotal`, and the total less the deductions is what is handed over.
 */

import { formatMoney, formatQuantity, type Centavos, type FrecuenciaPago, type Milli } from '@abastece/shared';
import {
  ETIQUETA_SALIDA,
  rangoDeCorrida,
  type LineaDeAguinaldo,
  type MotivoLiquidacion,
  type PagoAguinaldo,
  type Planilla,
  type PlanillaLinea,
} from '@abastece/core';
import { DocumentoPdf, anchoDeTexto, recortar, type Fuente } from './pdf.js';
import { ANCHO, encabezado, envolver, fila, type DatosNegocio } from './recibo.js';
import { normalizar } from './texto.js';

/** How a run, a settlement or the aguinaldo is handed over (`planilla.pagada`, `aguinaldo.pagado`). */
export type FormaDePagoPlanilla = 'efectivo_cordobas' | 'transferencia' | 'billetera';

/** The person's papers, from their file: the run's line keeps only the name and the job. */
export interface Papeles {
  cedula?: string | null;
  inssNumero?: string | null;
}

/** When and how it was handed over. */
export interface Entrega {
  fecha: string;
  /** Absent when it is not known here (the payment's event is not on this device): the day alone. */
  formaPago?: FormaDePagoPlanilla | undefined;
}

export interface OpcionesColilla {
  persona?: Papeles;
  /** Paid: the day and the way. Absent or null, the stub says it is still to be paid. */
  pago?: Entrega | null;
  /** A settlement's reason (`Empleado.motivoSalida`). */
  motivo?: MotivoLiquidacion | null;
  /** On paper: a line for them to sign that they received it. */
  firma?: boolean;
}

/** One amount on a stub, and what is said under it, if anything. */
export interface Renglon {
  etiqueta: string;
  monto: number;
  /** Said under it, not added to anything: «incluye 2 días de vacaciones». */
  debajo?: string;
}

/** What a stub says, whatever it is drawn on: the roll (`textoDeColilla`) or a page (`colillasEnPdf`). */
export interface Colilla {
  tipo: 'planilla' | 'liquidacion' | 'aguinaldo';
  /** «COLILLA DE PAGO», «LIQUIDACIÓN FINAL», «COMPROBANTE DE AGUINALDO». */
  titulo: string;
  /** Under the title: the run's schedule and days; the last day and why; «Décimo tercer mes». */
  detalle: string[];
  nombre: string;
  cargo: string | null;
  cedula: string | null;
  inss: string | null;
  /** What was earned, each amount that is not nothing (a run's salary always). */
  ingresos: Renglon[];
  /** What they add up to: the line's `brutoTotal`. */
  totalIngresos: number;
  /** Their INSS, the income tax withheld and the advances taken off, those that are not nothing. */
  deducciones: Renglon[];
  totalDeducciones: number;
  /** Said after what was earned: the aguinaldo's «Sin INSS ni IR.». */
  nota: string | null;
  /** What is handed over: what was earned less what came off. */
  recibe: number;
  pago: Entrega | null;
  /** The day it is of, as the books keep it: the run's last, the settlement's, the payment's. */
  dia: string;
}

const DE_LA_PLANILLA: Record<FrecuenciaPago, string> = {
  semanal: 'Planilla semanal',
  quincenal: 'Planilla quincenal',
  mensual: 'Planilla mensual',
};

const FORMA: Record<FormaDePagoPlanilla, string> = {
  efectivo_cordobas: 'Efectivo',
  transferencia: 'Transferencia',
  billetera: 'Billetera móvil',
};

/** «2026-10-31» as a stub says it, «31/10/2026»: the year whole, on a paper that is kept. */
const fecha = (f: string) => `${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(0, 4)}`;
const monto = (c: number) => formatMoney(c as Centavos, { symbol: false });
const dias = (milesimas: number) => `${formatQuantity(milesimas as Milli)} ${milesimas === 1000 ? 'día' : 'días'}`;
const papel = (s: string | null | undefined) => (s?.trim() ? s.trim() : null);

/**
 * One person's stub of a run or a settlement. A run says its days and how often it pays; a settlement,
 * the last day and why. Each amount on its own line, those that are nothing left out but the salary.
 */
export function colillaDePlanilla(
  corrida: Pick<Planilla, 'periodo' | 'desde' | 'hasta' | 'frecuencia' | 'esLiquidacion' | 'fecha'>,
  linea: PlanillaLinea,
  opciones: Omit<OpcionesColilla, 'firma'> = {},
): Colilla {
  const liquidacion = corrida.esLiquidacion;
  const { desde, hasta } = rangoDeCorrida(corrida);

  const ingresos: Renglon[] = [];
  // A settlement's salary is what was still owed of it, often nothing; a run's is its pay, and for
  // somebody paid by the day, the days it is for.
  if (!liquidacion || linea.bruto !== 0) {
    const salario = liquidacion ? 'Salario pendiente' : 'Salario';
    ingresos.push({
      etiqueta: linea.dias !== undefined ? `${salario} (${dias(linea.dias)})` : salario,
      monto: linea.bruto,
      // Days rested in the run, paid in the salary out of what was set aside for them: said, not added.
      ...(!liquidacion && linea.vacacionesPagadas === 0 && linea.diasVacaciones > 0
        ? { debajo: `incluye ${dias(linea.diasVacaciones)} de vacaciones` }
        : {}),
    });
  }
  if (linea.vacacionesPagadas !== 0) {
    ingresos.push({ etiqueta: linea.diasVacaciones > 0 ? `Vacaciones (${dias(linea.diasVacaciones)})` : 'Vacaciones', monto: linea.vacacionesPagadas });
  }
  if (linea.otrosGravables !== 0) ingresos.push({ etiqueta: 'Horas extra y otros', monto: linea.otrosGravables });
  // Neither pays INSS nor income tax: said, or the INSS would read as taken off too little.
  if (linea.aguinaldoPagado !== 0) ingresos.push({ etiqueta: 'Aguinaldo (exento)', monto: linea.aguinaldoPagado });
  if (linea.indemnizacion !== 0) ingresos.push({ etiqueta: 'Indemnización (exenta)', monto: linea.indemnizacion });

  // Their INSS, the income tax withheld, and the advances the run took off what it hands over.
  const adelanto = linea.adelantoDescontado ?? 0;
  const deducciones = [
    { etiqueta: 'INSS laboral', monto: linea.inssLaboral },
    { etiqueta: 'IR', monto: linea.irRetenido },
    { etiqueta: 'Adelanto de salario', monto: adelanto },
  ].filter((d) => d.monto !== 0);

  return {
    tipo: liquidacion ? 'liquidacion' : 'planilla',
    titulo: liquidacion ? 'LIQUIDACIÓN FINAL' : 'COLILLA DE PAGO',
    detalle: liquidacion
      ? [`Último día: ${fecha(corrida.fecha)}`, ...(opciones.motivo ? [`Motivo: ${ETIQUETA_SALIDA[opciones.motivo]}`] : [])]
      : [DE_LA_PLANILLA[corrida.frecuencia ?? 'mensual'], `Del ${fecha(desde)} al ${fecha(hasta)}`],
    nombre: linea.nombre,
    cargo: papel(linea.cargo),
    cedula: papel(opciones.persona?.cedula),
    inss: papel(opciones.persona?.inssNumero),
    ingresos,
    totalIngresos: linea.brutoTotal,
    deducciones,
    totalDeducciones: linea.inssLaboral + linea.irRetenido + adelanto,
    nota: null,
    recibe: linea.neto - adelanto,
    pago: opciones.pago ?? null,
    dia: liquidacion ? corrida.fecha : hasta,
  };
}

/**
 * One person's stub of an aguinaldo payment: the amount, that it pays no INSS nor income tax, and the
 * day and way it was paid. The line keeps no job: the one in their file, if given.
 */
export function colillaDeAguinaldo(
  pago: Pick<PagoAguinaldo, 'fecha' | 'formaPago'>,
  linea: Pick<LineaDeAguinaldo, 'nombre' | 'monto'>,
  opciones: { cargo?: string | null; persona?: Papeles } = {},
): Colilla {
  return {
    tipo: 'aguinaldo',
    titulo: 'COMPROBANTE DE AGUINALDO',
    detalle: ['Décimo tercer mes'],
    nombre: linea.nombre,
    cargo: papel(opciones.cargo),
    cedula: papel(opciones.persona?.cedula),
    inss: papel(opciones.persona?.inssNumero),
    ingresos: [{ etiqueta: 'Aguinaldo', monto: linea.monto }],
    totalIngresos: linea.monto,
    deducciones: [],
    totalDeducciones: 0,
    nota: 'Sin INSS ni IR.',
    recibe: linea.monto,
    pago: { fecha: pago.fecha, formaPago: pago.formaPago },
    dia: pago.fecha,
  };
}

// ---------------------------------------------------------------------------------------------
// On the roll
// ---------------------------------------------------------------------------------------------

const raya = () => '-'.repeat(ANCHO);

/**
 * The stub as the roll prints it. The day and the way it was paid each on a line of their own, as the
 * amounts are: said in one sentence, «Pagado el 31/10/2026, en efectivo.» is 34 characters and broke
 * over two lines. An aguinaldo's one amount is said alone, with no heading nor total.
 */
export function textoDeColilla(c: Colilla, negocio: DatosNegocio, conFirma = false): string {
  const l = [...encabezado(negocio), raya(), c.titulo, ...c.detalle.flatMap((d) => envolver(d)), raya()];
  l.push(...envolver(c.nombre));
  if (c.cargo) l.push(...envolver(c.cargo));
  if (c.cedula) l.push(...envolver(`Cédula: ${c.cedula}`));
  if (c.inss) l.push(...envolver(`No. INSS: ${c.inss}`));
  l.push(raya());

  const unoSolo = c.tipo === 'aguinaldo';
  if (!unoSolo) l.push('INGRESOS');
  for (const r of c.ingresos) {
    l.push(fila(r.etiqueta, monto(r.monto)));
    if (r.debajo) l.push(...envolver(r.debajo, ANCHO - 2).map((x) => `  ${x}`));
  }
  if (!unoSolo) l.push(fila('Total ingresos', monto(c.totalIngresos)));
  if (c.nota) l.push(...envolver(c.nota));
  if (c.deducciones.length > 0) {
    l.push(raya(), 'DEDUCCIONES');
    for (const d of c.deducciones) l.push(fila(d.etiqueta, monto(d.monto)));
    l.push(fila('Total deducciones', monto(c.totalDeducciones)));
  }

  l.push(raya(), fila('A RECIBIR', formatMoney(c.recibe as Centavos)), raya());
  if (c.pago) {
    l.push(fila('Pagado el', fecha(c.pago.fecha)));
    if (c.pago.formaPago) l.push(fila('Forma de pago', FORMA[c.pago.formaPago]));
  } else {
    l.push('Pendiente de pago.');
  }
  // On paper, room to sign under «Recibí conforme», and the name under the line.
  if (conFirma) l.push('', 'Recibí conforme:', '', '', '', '_'.repeat(ANCHO), ...envolver(c.nombre));
  return l.join('\n');
}

/** One person's stub of a run or a settlement, as the roll prints it (`colillaDePlanilla`). */
export const textoColilla = (
  corrida: Parameters<typeof colillaDePlanilla>[0],
  linea: PlanillaLinea,
  negocio: DatosNegocio,
  opciones: OpcionesColilla = {},
): string => textoDeColilla(colillaDePlanilla(corrida, linea, opciones), negocio, opciones.firma);

/** One person's stub of an aguinaldo payment, as the roll prints it (`colillaDeAguinaldo`). */
export const textoColillaAguinaldo = (
  pago: Pick<PagoAguinaldo, 'fecha' | 'formaPago'>,
  linea: Pick<LineaDeAguinaldo, 'nombre' | 'monto'>,
  negocio: DatosNegocio,
  opciones: { cargo?: string | null; persona?: Papeles; firma?: boolean } = {},
): string => textoDeColilla(colillaDeAguinaldo(pago, linea, opciones), negocio, opciones.firma);

/** Where one stub ends and the next begins on the roll: a line to tear or cut along, with room around it. */
export const CORTE = `\n\n\n${'- '.repeat(ANCHO / 2).trimEnd()}\n\n\n`;

/** Several stubs in one print: each whole, one after the other, a line to cut along between them. */
export const juntarColillas = (textos: readonly string[]): string => textos.join(CORTE);

// ---------------------------------------------------------------------------------------------
// On a page
// ---------------------------------------------------------------------------------------------

const MARGEN = 48;
/** The two tables side by side, what was earned and what came off, and the room between them. */
const ENTRE_TABLAS = 16;
const ALTO_FILA = 17;
const ALTO_DEBAJO = 12;

/**
 * Text cut into lines that fit `maximo` points, at the spaces, as the roll's `envolver` does by
 * characters: a name is never cut short on a paper somebody signs. Only a word longer than a whole
 * line loses its end, to «…».
 */
function enRenglones(s: string, maximo: number, fuente: Fuente, tamano: number): string[] {
  const lineas: string[] = [];
  let linea = '';
  for (const palabra of s.split(/\s+/).filter(Boolean)) {
    const junto = linea ? `${linea} ${palabra}` : palabra;
    if (anchoDeTexto(junto, fuente, tamano) <= maximo) {
      linea = junto;
      continue;
    }
    if (linea) lineas.push(linea);
    linea = recortar(palabra, maximo, fuente, tamano);
  }
  if (linea) lineas.push(linea);
  return lineas;
}

/** What the document is called where a reader names it: «Colilla de pago · Karla Mendoza · 31/10/2026». */
export function tituloDelPdf(colillas: readonly Colilla[]): string {
  const primera = colillas[0];
  if (!primera) return 'Colillas de pago';
  const uno = { planilla: 'Colilla de pago', liquidacion: 'Liquidación final', aguinaldo: 'Comprobante de aguinaldo' }[primera.tipo];
  const varios = { planilla: 'Colillas de pago', liquidacion: 'Liquidaciones', aguinaldo: 'Comprobantes de aguinaldo' }[primera.tipo];
  return colillas.length === 1 ? `${uno} · ${primera.nombre} · ${fecha(primera.dia)}` : `${varios} · ${fecha(primera.dia)}`;
}

/** A name as a file's name takes it: «María José Pérez» is «maria-jose-perez». */
export const nombreParaArchivo = (s: string): string => normalizar(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'colilla';

/**
 * The stubs on US Letter, a page each, for the shop's printer or to keep: the shop and what this is
 * across the top; the person, their job and papers; what was earned and what came off side by side,
 * their totals level; what is handed over in a band; the day and the way it was paid; and on the right,
 * a line to sign under, «Recibí conforme», with the name. The same words and figures the roll prints.
 */
export function colillasEnPdf(colillas: readonly Colilla[], negocio: DatosNegocio): DocumentoPdf {
  const pdf = new DocumentoPdf();
  const derecha = pdf.ancho - MARGEN;
  const util = derecha - MARGEN;
  const anchoTabla = (util - ENTRE_TABLAS) / 2;
  const contacto = [negocio.direccion?.trim(), negocio.telefono?.trim() ? `Tel: ${negocio.telefono.trim()}` : '', negocio.ruc?.trim() ? `RUC: ${negocio.ruc.trim()}` : '']
    .filter(Boolean)
    .join(' · ');

  for (const c of colillas) {
    pdf.nuevaPagina();
    let y = MARGEN;

    // What this is, at the right; the shop in the room it leaves, at the left, on as many lines as it takes.
    const anchoDerecha = Math.max(anchoDeTexto(c.titulo, 'negrita', 13), ...c.detalle.map((d) => anchoDeTexto(d, 'normal', 9)));
    pdf.texto(derecha, y + 13, c.titulo, { fuente: 'negrita', tamano: 13, alinear: 'derecha' });
    c.detalle.forEach((d, i) => pdf.texto(derecha, y + 28 + i * 12, d, { tamano: 9, gris: 0.35, alinear: 'derecha' }));
    const anchoIzquierda = util - anchoDerecha - 24;
    /** The baseline of the last line drawn at the left. */
    let izquierda = y - 3;
    for (const t of enRenglones(negocio.nombre.toUpperCase(), anchoIzquierda, 'negrita', 15)) {
      izquierda += 17;
      pdf.texto(MARGEN, izquierda, t, { fuente: 'negrita', tamano: 15 });
    }
    (contacto ? enRenglones(contacto, anchoIzquierda, 'normal', 8.5) : []).forEach((t, i) => {
      izquierda += i === 0 ? 14 : 11;
      pdf.texto(MARGEN, izquierda, t, { tamano: 8.5, gris: 0.4 });
    });
    y = Math.max(izquierda, y + 28 + (c.detalle.length - 1) * 12) + 18;
    pdf.linea(MARGEN, y, derecha, y, { grosor: 1.2, gris: 0.15 });

    // Who: the name across the page, then the job and the papers it has, each in a third of it.
    y += 18;
    pdf.texto(MARGEN, y, 'Nombre', { tamano: 8, gris: 0.45 });
    for (const t of enRenglones(c.nombre, util, 'negrita', 12)) {
      y += 15;
      pdf.texto(MARGEN, y, t, { fuente: 'negrita', tamano: 12 });
    }
    const datos: [etiqueta: string, valor: string][] = [];
    if (c.cargo) datos.push(['Cargo', c.cargo]);
    if (c.cedula) datos.push(['Cédula', c.cedula]);
    if (c.inss) datos.push(['No. INSS', c.inss]);
    if (datos.length > 0) {
      y += 22;
      const tercio = util / 3;
      let alto = 14;
      datos.forEach(([etiqueta, valor], i) => {
        const x = MARGEN + i * tercio;
        pdf.texto(x, y, etiqueta, { tamano: 8, gris: 0.45 });
        const lineas = enRenglones(valor, tercio - 12, 'normal', 10.5);
        lineas.forEach((t, j) => pdf.texto(x, y + 14 + j * 13, t, { tamano: 10.5 }));
        alto = Math.max(alto, 14 + (lineas.length - 1) * 13);
      });
      y += alto;
    }

    // What was earned and what came off, side by side; their totals on one line.
    y += 22;
    const altoDe = (renglones: readonly Renglon[]) => renglones.reduce((a, r) => a + ALTO_FILA + (r.debajo ? ALTO_DEBAJO : 0), 0);
    const filas = Math.max(altoDe(c.ingresos), c.deducciones.length > 0 ? altoDe(c.deducciones) : ALTO_FILA);
    const tabla = (x: number, encabezadoDeTabla: string, renglones: readonly Renglon[], total: string, cuanto: number) => {
      pdf.banda(x, y, anchoTabla, 18, 0.92);
      pdf.texto(x + 8, y + 12.5, encabezadoDeTabla, { fuente: 'negrita', tamano: 8.5, gris: 0.25 });
      pdf.texto(x + anchoTabla - 8, y + 12.5, 'C$', { fuente: 'negrita', tamano: 8.5, gris: 0.25, alinear: 'derecha' });
      let arriba = y + 18;
      if (renglones.length === 0) {
        pdf.texto(x + 8, arriba + 12, 'Ninguna', { tamano: 10, gris: 0.5 });
      }
      for (const r of renglones) {
        const cifra = monto(r.monto);
        const anchoCifra = anchoDeTexto(cifra, 'normal', 10);
        pdf.texto(x + 8, arriba + 12, recortar(r.etiqueta, anchoTabla - 24 - anchoCifra, 'normal', 10), { tamano: 10 });
        pdf.texto(x + anchoTabla - 8, arriba + 12, cifra, { tamano: 10, alinear: 'derecha' });
        arriba += ALTO_FILA;
        if (r.debajo) {
          pdf.texto(x + 16, arriba + 7, recortar(r.debajo, anchoTabla - 24, 'normal', 8.5), { tamano: 8.5, gris: 0.4 });
          arriba += ALTO_DEBAJO;
        }
        pdf.linea(x, arriba, x + anchoTabla, arriba, { grosor: 0.4, gris: 0.85 });
      }
      const base = y + 18 + filas;
      pdf.linea(x, base, x + anchoTabla, base, { grosor: 0.9, gris: 0.3 });
      pdf.texto(x + 8, base + 13, total, { fuente: 'negrita', tamano: 10 });
      pdf.texto(x + anchoTabla - 8, base + 13, monto(cuanto), { fuente: 'negrita', tamano: 10, alinear: 'derecha' });
    };
    tabla(MARGEN, 'INGRESOS', c.ingresos, 'Total ingresos', c.totalIngresos);
    tabla(MARGEN + anchoTabla + ENTRE_TABLAS, 'DEDUCCIONES', c.deducciones, 'Total deducciones', c.totalDeducciones);
    y += 18 + filas + 20;
    if (c.nota) {
      y += 12;
      pdf.texto(MARGEN, y, c.nota, { tamano: 9, gris: 0.4 });
    }

    // What is handed over, then the day and the way, or that it still is not.
    y += 14;
    pdf.banda(MARGEN, y, util, 32, 0.88);
    pdf.texto(MARGEN + 12, y + 20.5, 'A RECIBIR', { fuente: 'negrita', tamano: 12 });
    pdf.texto(derecha - 12, y + 21, formatMoney(c.recibe as Centavos), { fuente: 'negrita', tamano: 15, alinear: 'derecha' });
    y += 32 + 20;
    const pago = c.pago
      ? `Pagado el ${fecha(c.pago.fecha)}${c.pago.formaPago ? ` · Forma de pago: ${FORMA[c.pago.formaPago]}` : ''}`
      : 'Pendiente de pago.';
    pdf.texto(MARGEN, y, pago, { tamano: 10, gris: c.pago ? 0 : 0.35 });

    // Room to sign, on the right half: the line, «Recibí conforme», the name and the cédula.
    y += 64;
    const firmaX = MARGEN + anchoTabla + ENTRE_TABLAS;
    pdf.linea(firmaX, y, derecha, y, { grosor: 0.8, gris: 0.2 });
    pdf.texto(firmaX, y + 13, 'Recibí conforme', { tamano: 9, gris: 0.4 });
    y += 13;
    for (const t of enRenglones(c.nombre, anchoTabla, 'negrita', 10)) {
      y += 14;
      pdf.texto(firmaX, y, t, { fuente: 'negrita', tamano: 10 });
    }
    if (c.cedula) pdf.texto(firmaX, y + 13, `Cédula: ${c.cedula}`, { tamano: 9, gris: 0.35 });
  }
  return pdf;
}
